import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type PropsWithChildren,
} from 'react';
import { rpcClient } from 'src/api/rpc/client';
import type { ModuleKey } from '@erp/rpc';

export interface FieldWidgetData {
  name: string;
  description: string;
}

export interface MemoryBankNoteWidgetData {
  content: string;
  entityId: string;
  entityType: string;
  entityLabel: string;
  metadata?: Record<string, unknown>;
}

export interface MessageWidget {
  type: 'field' | 'memoryBankNote';
  data: FieldWidgetData | MemoryBankNoteWidgetData;
  status: 'pending' | 'confirmed' | 'rejected';
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  hidden?: boolean;
  /** Auxiliary content for tool actions like comparisons */
  auxiliaryContent?: {
    toolCallId?: string;
    action?: string;
    payload?: unknown;
  };
  /** Widget associated with this message (for tool call results) */
  widget?: MessageWidget;
}

export interface ToolCall {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  result?: unknown;
  status: 'pending' | 'complete' | 'error';
}

export interface ResolvedWidgetData {
  name: string;
  description: string;
}

export interface ResolvedWidget {
  type: 'field' | 'memoryBankNote';
  data: ResolvedWidgetData | MemoryBankNoteWidgetData;
  status: 'confirmed' | 'rejected';
  timestamp: number;
}

export interface AgentStateContextData {
  messages: Message[];
  streamingText: string;
  isLoading: boolean;
  isLoadingHistory: boolean;
  error: string | null;
  activeToolCalls: ToolCall[];
  currentThreadId: string | null;
  currentModuleKey: ModuleKey | null;
  resolvedWidgets: ResolvedWidget[];
  sendMessage: (
    content: string,
    moduleKey: ModuleKey,
    currentPage: string,
    options?: { hidden?: boolean },
  ) => Promise<void>;
  /** Add a local message without sending to backend (for demo/local command handling) */
  addLocalMessage: (role: 'user' | 'assistant', content: string) => void;
  addResolvedWidget: (widget: ResolvedWidget) => void;
  /** Attach a widget to the last assistant message */
  attachWidgetToLastMessage: (widget: Omit<MessageWidget, 'status'>) => void;
  /** Update the status of a widget on a message */
  updateWidgetStatus: (
    messageId: string,
    status: 'confirmed' | 'rejected',
  ) => void;
  reset: () => void;
  cancel: () => void;
}

const AgentStateContext = createContext<AgentStateContextData | undefined>(
  undefined,
);

let messageIdCounter = 0;

function generateMessageId(): string {
  return `msg-${Date.now()}-${++messageIdCounter}`;
}

export function AgentStateProvider({ children }: PropsWithChildren) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeToolCalls, setActiveToolCalls] = useState<ToolCall[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [currentModuleKey, setCurrentModuleKey] = useState<ModuleKey | null>(
    null,
  );
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [resolvedWidgets, setResolvedWidgets] = useState<ResolvedWidget[]>([]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const streamSessionIdRef = useRef(0);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsLoading(false);
  }, []);

  const sendMessage = useCallback(
    async (
      content: string,
      moduleKey: ModuleKey,
      currentPage: string,
      options?: { hidden?: boolean },
    ) => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      const myStreamSession = ++streamSessionIdRef.current;

      // Check if module changed - if so, start fresh conversation
      const moduleChanged =
        currentModuleKey !== null && currentModuleKey !== moduleKey;
      if (moduleChanged) {
        setMessages([]);
        setCurrentThreadId(null);
        setResolvedWidgets([]);
      }
      setCurrentModuleKey(moduleKey);

      const userMessage: Message = {
        id: generateMessageId(),
        role: 'user',
        content,
        timestamp: new Date(),
        hidden: options?.hidden,
      };

      // Use fresh messages array if module changed
      const existingMessages = moduleChanged ? [] : messages;
      setMessages((prev) =>
        moduleChanged ? [userMessage] : [...prev, userMessage],
      );
      setStreamingText('');
      setIsLoading(true);
      setError(null);
      setActiveToolCalls([]);

      try {
        const apiMessages = [...existingMessages, userMessage].map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const iterator = await rpcClient.ai.foregroundAgent.chat(
          {
            moduleKey,
            currentPage,
            threadId: moduleChanged
              ? undefined
              : (currentThreadId ?? undefined),
            messages: apiMessages,
          },
          { signal: abortControllerRef.current.signal },
        );

        let fullText = '';

        for await (const event of iterator) {
          if (myStreamSession !== streamSessionIdRef.current) {
            break;
          }

          switch (event.type) {
            case 'text-delta':
              fullText += event.text;
              setStreamingText(fullText);
              break;

            case 'tool-call':
              setActiveToolCalls((prev) => [
                ...prev,
                {
                  toolCallId: event.toolCallId,
                  toolName: event.toolName,
                  args: event.args,
                  status: 'pending',
                },
              ]);
              break;

            case 'tool-result':
              setActiveToolCalls((prev) =>
                prev.map((tc) =>
                  tc.toolCallId === event.toolCallId
                    ? {
                        ...tc,
                        result: event.result,
                        status: 'complete' as const,
                      }
                    : tc,
                ),
              );
              break;

            case 'thread-info':
              setCurrentThreadId(event.threadId);
              break;

            case 'finish':
              if (fullText) {
                const assistantMessage: Message = {
                  id: generateMessageId(),
                  role: 'assistant',
                  content: fullText,
                  timestamp: new Date(),
                };
                setMessages((prev) => [...prev, assistantMessage]);
              }
              setStreamingText('');
              break;

            case 'error':
              setError(event.message);
              break;
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError(err instanceof Error ? err.message : 'Stream error');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [messages, currentThreadId, currentModuleKey],
  );

  const addResolvedWidget = useCallback((widget: ResolvedWidget) => {
    setResolvedWidgets((prev) => [...prev, widget]);
  }, []);

  const attachWidgetToLastMessage = useCallback(
    (widget: Omit<MessageWidget, 'status'>) => {
      setMessages((prev) => {
        // Find the last assistant message
        const lastIndex = prev.length - 1;
        for (let i = lastIndex; i >= 0; i--) {
          if (prev[i].role === 'assistant') {
            const updated = [...prev];
            updated[i] = {
              ...updated[i],
              widget: { ...widget, status: 'pending' },
            };
            return updated;
          }
        }
        return prev;
      });
    },
    [],
  );

  const updateWidgetStatus = useCallback(
    (messageId: string, status: 'confirmed' | 'rejected') => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId && msg.widget
            ? { ...msg, widget: { ...msg.widget, status } }
            : msg,
        ),
      );
    },
    [],
  );

  const addLocalMessage = useCallback(
    (role: 'user' | 'assistant', content: string) => {
      const message: Message = {
        id: generateMessageId(),
        role,
        content,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, message]);
    },
    [],
  );

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    setMessages([]);
    setStreamingText('');
    setIsLoading(false);
    setIsLoadingHistory(false);
    setError(null);
    setActiveToolCalls([]);
    setCurrentThreadId(null);
    setCurrentModuleKey(null);
    setResolvedWidgets([]);
    streamSessionIdRef.current++;
  }, []);

  return (
    <AgentStateContext.Provider
      value={{
        messages,
        streamingText,
        isLoading,
        isLoadingHistory,
        error,
        activeToolCalls,
        currentThreadId,
        currentModuleKey,
        resolvedWidgets,
        sendMessage,
        addLocalMessage,
        addResolvedWidget,
        attachWidgetToLastMessage,
        updateWidgetStatus,
        reset,
        cancel,
      }}
    >
      {children}
    </AgentStateContext.Provider>
  );
}

export function useAgentState() {
  const context = useContext(AgentStateContext);
  if (context === undefined) {
    throw new Error('useAgentState must be used within an AgentStateProvider');
  }
  return context;
}
